import { memo, useCallback, useMemo, useState } from 'react';

import { Check, Close } from '@air/next-icons';
import { Chip } from '@air/primitive-chip';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputTrigger,
  type ComboboxInputTriggerProps,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
} from '@air/primitive-combobox';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { type TagBase, type TagSelectOption } from '~/components/Tags/utils';

export type TagsSelectProps = {
  id?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onCreateTag?: (name: string) => void;
  onInputChange: (value: string) => void;
  onTagsSelectionChange: (newTags: TagSelectOption[]) => void;
  placeholder?: string;
  selectedTags: TagSelectOption[];
  tags: TagBase[];
  hideChipsInInput?: boolean;
} & Pick<ComboboxInputTriggerProps, 'className'>;

export const TagsSelect = memo(
  ({
    disabled = false,
    id = 'tag',
    tags,
    onCreateTag: _onCreateTag,
    onInputChange,
    onTagsSelectionChange,
    isLoading = false,
    selectedTags = [],
    placeholder = 'Search for tags',
    hideChipsInInput = false,
  }: TagsSelectProps) => {
    const [searchValue, setSearchValue] = useState('');
    const onValueChange = useCallback(
      (value: string) => {
        setSearchValue(value);
        onInputChange(value);
      },
      [onInputChange],
    );

    const onCreateTag = useCallback(
      (value: string) => {
        _onCreateTag?.(value);
        onValueChange('');
      },
      [_onCreateTag, onValueChange],
    );

    const onRemoveTag = useCallback(
      (tag: TagSelectOption) => {
        onTagsSelectionChange?.(selectedTags.filter((t) => t.value !== tag.value));
      },
      [onTagsSelectionChange, selectedTags],
    );

    const onTagClick = useCallback(
      (tag: TagBase) => {
        if (selectedTags.some((t) => t.value === tag.id)) {
          onRemoveTag({ value: tag.id, label: tag.label });
        } else {
          onTagsSelectionChange?.([...selectedTags, { value: tag.id, label: tag.label }]);
        }
        onValueChange('');
      },
      [onRemoveTag, onTagsSelectionChange, selectedTags, onValueChange],
    );

    const showCreateTag = useMemo(
      () => searchValue && !tags.some((tag) => tag.label === searchValue) && !isLoading,
      [tags, searchValue, isLoading],
    );

    return (
      <Combobox>
        <ComboboxInputTrigger asChild data-id={id} disabled={disabled} size="large">
          <div>
            {hideChipsInInput || selectedTags.length === 0 ? (
              <div className="text-grey-10">{placeholder}</div>
            ) : (
              selectedTags.map((tag) => (
                <Chip data-id={tag.value} key={tag.value} shape="rounded" size="medium">
                  <span className="truncate">{tag.label}</span>
                  <IconButton
                    appearance="ghost"
                    color="grey"
                    data-testid="REMOVE_TAG_BUTTON"
                    data-id={tag.value}
                    icon={Close}
                    label="Remove smart tag"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveTag(tag);
                    }}
                    size="extra-small"
                  />
                </Chip>
              ))
            )}
          </div>
        </ComboboxInputTrigger>
        <ComboboxContent
          className="max-h-[min(320px,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)]"
          sideOffset={4}
          shouldFilter={false}
        >
          <ComboboxInput onValueChange={onValueChange} placeholder="Search tags..." value={searchValue} />

          {isLoading ? <ComboboxLoading>Loading...</ComboboxLoading> : <ComboboxEmpty>No tags found.</ComboboxEmpty>}

          <ComboboxList>
            {tags.map((tag) => {
              const isSelected = selectedTags.some((t) => t.value === tag.id);

              return (
                <ComboboxItem data-id={tag.id} onSelect={() => onTagClick(tag)} key={tag.id}>
                  {isSelected ? <Check className="size-4 shrink-0 text-blue-9" /> : <div className="size-4 shrink-0" />}
                  <div className={cn('truncate', isSelected ? 'opacity-70' : '')}>{tag.label}</div>
                </ComboboxItem>
              );
            })}
            {!!showCreateTag && onCreateTag && (
              <ComboboxItem data-id="create-tag" onSelect={() => onCreateTag(searchValue)}>
                {!!tags.length && <div className="size-4 shrink-0" />}
                <span className="truncate">Create &quot;{searchValue}&quot;?</span>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  },
);

TagsSelect.displayName = 'TagsSelect';
